import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';

import { cn } from '@/utils/utils';

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}
function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}
function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}
function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    data-slot="dialog-overlay"
    className={cn(
      'fixed inset-0 z-50 bg-black/30 dark:bg-gray-600/20 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-y-auto max-h-screen grid place-items-center',
      className
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * True when an "interact outside" event originated inside a *different* dialog
 * than the one handling it — i.e. one stacked above it.
 *
 * Radix only fires this event on layers whose own content does not contain the
 * target, so any hit here necessarily belongs to another dialog, and a layer
 * underneath must never dismiss from it. Without this guard, a single tap on
 * the top modal's close button closes BOTH layers: the top one via its own
 * close button, and the one underneath via pointer-down-outside (Radix defers
 * that dispatch to `click` for touch, by which time the top layer has already
 * unmounted and no longer shields the one below). Because the maquinista
 * modals are route-driven and close with `history.goBack()`, that second close
 * consumed a second history entry and threw the user out of the app (BIL-4862).
 */
export const isInteractionInsideAnotherDialog = (
  target: Element | null
): boolean =>
  target instanceof Element &&
  !!target.closest(
    '[data-slot="dialog-content"], [data-slot="alert-dialog-content"]'
  );

const DialogContent = ({
  className,
  children,
  external,
  autoFocus,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  external?: React.ReactNode;
}) => (
  <DialogPortal>
    <DialogOverlay>
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'z-40 relative grid w-full max-w-lg gap-4 bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg md:w-full',
          className
        )}
        {...props}
        onOpenAutoFocus={(event) => {
          if (autoFocus === false) event.preventDefault();
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.body.style.pointerEvents = '';
        }}
        onInteractOutside={(event) => {
          const oe = event.detail.originalEvent as
            | MouseEvent
            | PointerEvent
            | undefined;
          const originalTarget = (oe?.target || null) as Element | null;

          // 1) Keep existing behavior: ignore interactions coming from elements marked as external
          const external = document.querySelector('.external');
          if (external && originalTarget && external.contains(originalTarget)) {
            event.preventDefault();
          }

          // 2) Ignore interactions coming from Sonner toasts
          if (
            originalTarget instanceof Element &&
            originalTarget.closest('[data-sonner-toast]')
          ) {
            event.preventDefault();
          }

          // 3) Ignore interactions that happened inside ANOTHER dialog stacked
          //    above this one — see isInteractionInsideAnotherDialog.
          if (isInteractionInsideAnotherDialog(originalTarget)) {
            event.preventDefault();
          }

          // 4) Do not close when the user interacts with the browser scrollbars
          //    Works for both classic scrollbars (non-overlay) and macOS overlay scrollbars.
          //    - Classic: click lands outside the content box (offset beyond client rect)
          //    - Overlay: pointer is within a thin band near the viewport right/bottom edge
          //      while that axis is scrollable.
          const html = document.documentElement;
          const isScrollableY = html.scrollHeight - 1 > window.innerHeight;
          const isScrollableX = html.scrollWidth - 1 > window.innerWidth;

          if (oe) {
            // Classic scrollbar heuristic: if available, compare against the overlay's width.
            // (When scrollbars take layout space, clicks can register just outside the overlay area.)
            // We fallback to overlay detection below if this isn't conclusive.

            // Overlay scrollbar heuristic band (in CSS px)
            const BAND = 16; // safe band near edges where overlay scrollbars appear

            const nearRightEdge = oe.clientX >= window.innerWidth - BAND;
            const nearBottomEdge = oe.clientY >= window.innerHeight - BAND;

            const classicScrollbarClick = (() => {
              // If the pointer is strictly to the right/bottom of the document's content area,
              // consider it a scrollbar interaction. This is a coarse check but safe.
              const beyondRight = oe.clientX > html.clientWidth;
              const beyondBottom = oe.clientY > html.clientHeight;
              return (
                (beyondRight && isScrollableY) ||
                (beyondBottom && isScrollableX)
              );
            })();

            const overlayScrollbarGesture =
              (nearRightEdge && isScrollableY) ||
              (nearBottomEdge && isScrollableX);

            if (classicScrollbarClick || overlayScrollbarGesture) {
              event.preventDefault();
            }
          }

          // If you are wrapping this component like shadcn, also call the caller's handler
          props.onInteractOutside?.(event);
        }}
      >
        {children}
        <DialogPrimitive.Close className="screenshot:hidden absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <Cross2Icon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
      <div className="external contents">{external}</div>
    </DialogOverlay>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    data-slot="dialog-header"
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left!',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end gap-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    data-slot="dialog-title"
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    data-slot="dialog-description"
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
